/**
 * "Open this booking" — the address a chat event card's **View booking** button
 * navigates to: `/bookings?bookingId=…&start=…&coach=…`.
 *
 * Only `bookingId` is the address. `start` (when the call is) and `coach` (whose
 * calendar it is on, an `Employees` id) are hints that save a lookup: the
 * bookings screen loads a month at a time, for one coach at a time, and there is
 * no endpoint that finds a booking by id alone. A link without them still opens
 * the screen; it just cannot jump to the call.
 *
 * Kept free of React and the router so it can be tested as plain functions.
 */

const UUID = /^[0-9a-f]{8}-[0-9a-f]{4}-[1-5][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i;

const parseDate = (value) => {
  if (!value) return null;
  const date = new Date(value);
  return Number.isNaN(date.getTime()) ? null : date;
};

/**
 * @typedef {Object} BookingFocus
 * @property {string} bookingId
 * @property {Date|null} start - Which month and day to show.
 * @property {string|null} coachEmployeeId - Whose calendar the call is on.
 */

/**
 * @param {URLSearchParams} searchParams
 * @returns {BookingFocus|null} Null when the URL names no (valid) booking.
 */
export const parseBookingFocus = (searchParams) => {
  const bookingId = (searchParams?.get("bookingId") || "").trim();
  // The id ends up in a DOM selector and a list lookup. Anything that is not a
  // booking id is not a booking link.
  if (!UUID.test(bookingId)) return null;
  const coach = (searchParams.get("coach") || "").trim();
  return {
    bookingId,
    start: parseDate(searchParams.get("start")),
    coachEmployeeId: UUID.test(coach) ? coach : null,
  };
};

/**
 * Whether the calendar is showing the month the focused call is in — the
 * precondition for looking the call up in that month's rows.
 *
 * @param {BookingFocus} focus
 * @param {{ year: number, month: number }} view - `month` is 0-indexed.
 * @returns {boolean}
 */
export const isShowingFocusMonth = (focus, view) =>
  !focus.start ||
  (focus.start.getFullYear() === view.year && focus.start.getMonth() === view.month);

/**
 * @param {Object[]} bookings - The loaded month's rows.
 * @param {BookingFocus} focus
 * @returns {Object|null}
 */
export const findFocusedBooking = (bookings, focus) =>
  (bookings || []).find((record) => String(record?.id) === focus.bookingId) || null;
